import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
// import { LoadingSpinner } from '../../components/LoadingSpinner';
import { forgotPasswordReset } from '../../services/api';
import logo from "../../assets/img/logo.png";

export const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: '',
    });

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setError('Invalid reset link. Please request a new password reset.');
        }
    }, [token]);

    const validatePasswordForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.newPassword) {
            newErrors.newPassword = 'New password is required.';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long.';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Confirm password is required.';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'newPassword') {
            if (!value) {
                newErrors.newPassword = 'New password is required.';
            } else if (value.length < 8) {
                newErrors.newPassword = 'Password must be at least 8 characters long.';
            } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(value)) {
                newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
            } else {
                delete newErrors.newPassword;
            }

            // Also validate confirm password when new password changes
            if (formData.confirmPassword && formData.confirmPassword !== value) {
                newErrors.confirmPassword = 'Passwords do not match';
            } else if (formData.confirmPassword && formData.confirmPassword === value) {
                delete newErrors.confirmPassword;
            }
        } else if (fieldName === 'confirmPassword') {
            if (!value) {
                newErrors.confirmPassword = 'Confirm password is required.';
            } else if (formData.newPassword !== value) {
                newErrors.confirmPassword = 'Passwords do not match';
            } else {
                delete newErrors.confirmPassword;
            }
        }

        setErrors(newErrors);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Real-time validation
        validatePasswordField(name, value);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setErrors({});
        setLoading(true);

        // Validation using the new validation function
        if (!validatePasswordForm()) {
            setLoading(false);
            return;
        }

        try {
            await forgotPasswordReset(token!, formData.newPassword, formData.confirmPassword);
            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    if (!token) {
        return (
            <div className='auth-wrapper'>
                <div className='auth-box'>
                    <div className='auth-header text-center'>
                        <img className='auth-logo mb-2' src={logo} alt="Renegade Logo" />
                        <h1 className='mb-2'>Invalid Reset Link</h1>
                        <p className='text-16-reg grey-500 mb-4'>This password reset link is invalid or has expired.</p>
                    </div>
                    <div className="auth-body">
                        <button
                            onClick={() => navigate('/forgot-password')}
                            className='renegade-btn-primary w-100 mt-5'
                        >
                            Request New Reset Link
                        </button>
                    </div>
                </div>
            </div>

        );
    }

    if (success) {
        return (
            <>
                <div className='auth-wrapper'>
                    <div className='auth-box'>
                        <div className='auth-header text-center'>
                            <img className='auth-logo mb-2' src={logo} alt="Renegade Logo" />
                            <h1 className='mb-2'>Password Reset Successful!</h1>
                            <p className='text-16-reg grey-500 mb-4'>Your password has been successfully reset. You will be redirected to the login page shortly.</p>
                            <svg className='mx-auto mb-4' width="129" height="5" viewBox="0 0 129 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.5" width="81" height="5" rx="2.5" fill="#DA291C" />
                                <rect x="88.5" width="22" height="5" rx="2.5" fill="#BDBDBD" />
                            </svg>
                        </div>
                        <div className="auth-body">
                            <button
                                onClick={() => navigate('/login')}
                                className='renegade-btn-primary w-100 mt-5'
                            >
                                Go to Login
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className='auth-wrapper'>
                <div className='auth-box reset-password-box'>
                    <div className='auth-header text-center'>
                        <img className='auth-logo mb-2' src={logo} alt="Renegade Logo" />
                        <h1 className='mb-2'>Reset Your Password</h1>
                        <p className='text-16-reg grey-500 mb-4'>Enter your new password below.</p>
                        <svg className='mx-auto mb-4' width="129" height="5" viewBox="0 0 129 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" width="81" height="5" rx="2.5" fill="#DA291C" />
                            <rect x="88.5" width="22" height="5" rx="2.5" fill="#BDBDBD" />
                        </svg>
                    </div>
                    <div className="auth-body">
                        <form onSubmit={handleSubmit}>
                            <div className="form-group password-feild">
                                <input
                                    id="newPassword"
                                    name="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    placeholder="Enter New Password"
                                    value={formData.newPassword}
                                    onChange={handleInputChange}
                                    className={`form-control ${errors.newPassword ? 'border-danger' : ''}`}
                                />
                                <button type="button" onClick={toggleNewPasswordVisibility} className='password-toggle'>
                                    {showNewPassword ? (
                                        <i className="iconoir-eye"></i>
                                    ) : (
                                        <i className="iconoir-eye-closed"></i>
                                    )}
                                </button>
                                {errors.newPassword && (
                                    <p className="error-message">{errors.newPassword}</p>
                                )}
                            </div>


                            <div className="form-group password-feild">
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Enter Confirm Password"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    className={`form-control ${errors.confirmPassword ? 'border-danger' : ''}`}
                                />
                                <button type="button" onClick={toggleConfirmPasswordVisibility} className='password-toggle'>
                                    {showConfirmPassword ? (
                                        <i className="iconoir-eye"></i>
                                    ) : (
                                        <i className="iconoir-eye-closed"></i>
                                    )}
                                </button>
                                {errors.confirmPassword && (
                                    <p className="error-message">{errors.confirmPassword}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                className='renegade-btn-primary w-100 mt-5'
                                disabled={loading}
                            >
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

        </>
    );
};

export default ResetPasswordPage;
